import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  ChangeDetectorRef,
  AfterViewChecked,
  Renderer2,
} from "@angular/core";
import { ViewChild } from "@angular/core";

import { BfxApplicationNotificationService } from "./services/bfx-application-notification.service";
import { BfxApplicationNotificationData } from "./models/bfx-application-notification-data.model";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { NativeDesktopAppNotification } from "../bfx-native-desktop/native-desktop-app-notification";

import { LoggerService } from "@bfx/logger";
import { OverlayPanel } from "@bfx/primeng/overlaypanel";
import { DataList } from "@bfx/primeng/datalist";
import { Table } from "@bfx/primeng/table";
const logger = LoggerService.for(__dirname);

// narrower windows can be ~30px off this, but perfer safety
const TINY_WINDOW_TRESHOLD = 240;
const SMALL_WINDOW_TRESHOLD = 400;
const NORMAL_WINDOW_TRESHOLD = 550;
const LARGE_WINDOW_TRESHOLD = 700;
const HUGE_WINDOW_TRESHOLD = 900;

const TINY_PAGINATION_ROW_COUNT = 1;
const SMALL_PAGINATION_ROW_COUNT = 3;
const NORMAL_PAGINATION_ROW_COUNT = 5;
const LARGE_PAGINATION_ROW_COUNT = 7;
const HUGE_PAGINATION_ROW_COUNT = 10;

@Component({
  selector: "bfx-notification-viewer",
  templateUrl: "./html/bfx-notification-viewer.html",
  standalone: false,
})
export class BfxNotificationViewerComponent implements AfterViewChecked, OnInit, OnDestroy {
  public notifications: Array<BfxApplicationNotificationData> = [];
  public numRows: number = LARGE_PAGINATION_ROW_COUNT;
  private paginateOnResize = false;
  private unlistenResizeFn = null;

  // Pagination properties
  public pageSize: number = 10;
  public first: number = 0;
  public pageNo: number = 1;

  @ViewChild("dl", { static: true }) dl: Table;

  @Input()
  overlayPanel: OverlayPanel;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(
    private _appNotifyService: BfxApplicationNotificationService,
    private layoutService: GoldenLayoutService,
    private cd: ChangeDetectorRef,
    renderer2: Renderer2,
  ) {
    if (NativeDesktopSupport.isNativeDesktopContainer() == false) {
      this.unlistenResizeFn = renderer2.listen(window, "resize", (event) =>
        this.updateNumRows(event),
      );
      this.updateNumRows();
    }
  }

  updateNumRows(event?): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) return;

    let oldNumRows = this.numRows;
    let height = window.innerHeight * NativeDesktopSupport.zoomLevel;
    this.numRows =
      height >= HUGE_WINDOW_TRESHOLD
        ? HUGE_PAGINATION_ROW_COUNT
        : height >= LARGE_WINDOW_TRESHOLD
          ? LARGE_PAGINATION_ROW_COUNT
          : height >= NORMAL_WINDOW_TRESHOLD
            ? NORMAL_PAGINATION_ROW_COUNT
            : height >= SMALL_WINDOW_TRESHOLD
              ? SMALL_PAGINATION_ROW_COUNT
              : TINY_PAGINATION_ROW_COUNT;

    if (window.innerHeight < TINY_WINDOW_TRESHOLD)
      logger.out.warn(
        `${BfxNotificationViewerComponent.constructor.name} not enough room to display single app-notification + paginator`,
      );

    if (this.paginateOnResize && oldNumRows != this.numRows) {
      this.cd.detectChanges();
      // this.dl.updatePaginator();
      // this.dl.paginate({ first: this.first, rows: this.numRows });
    }
  }

  ngAfterViewChecked(): void {
    this.paginateOnResize = true;
  }

  ngOnInit() {
    this._appNotifyService.setPaginatorCallbackFunction(this.resetPaginator.bind(this));
    this.notifications = this._appNotifyService.getApplicationNotifications();
  }

  public dismissAllNotifications(): void {
    this._appNotifyService.closeAllNotifications();

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      // call before overlay hide, to avoid animation driven flash of background
      NativeDesktopAppNotification.hide();
    }

    this.overlayPanel.hide();
  }

  public closeNotifications(): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      // call before overlay hide, to avoid animation driven flash of background
      NativeDesktopAppNotification.hide();
    }

    this.overlayPanel.hide();
  }

  public showSettings(): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      // call before overlay hide, to avoid animation driven flash of background
      NativeDesktopAppNotification.hide();
    }

    this.layoutService.showSettings("Notifications-Application");

    this.overlayPanel.hide();
  }

  ngOnDestroy() {
    if (this.unlistenResizeFn != null) this.unlistenResizeFn();

    this.unlistenResizeFn = null;
  }

  public resetPaginator(): void {
    this.first = 0;
    this.pageNo = 1;
    this.cd.detectChanges();

    // if (this.dl.hasOwnProperty("first") && this.dl.hasOwnProperty("rows")) {
    //   this.dl.updatePaginator();
    //   this.dl.paginate({ first: 0, rows: this.numRows });
    // }
  }

  // Pagination methods
  public isFirstPage(): boolean {
    return this.pageNo == 1;
  }
  public isLastPage(): boolean {
    return this.pageNo == this.pageCount;
  }

  public previousPage(): void {
    if (this.pageNo > 1) {
      this.pageNo--;
      this.first -= this.pageSize;
    }
  }
  public nextPage(): void {
    if (this.pageNo < this.pageCount) {
      this.pageNo++;
      this.first += this.pageSize;
    }
  }

  public get pageCount(): number {
    if (this.notifications.length > 0) {
      return Math.ceil(this.notifications.length / this.pageSize);
    }
    return 1;
  }
}
